import {
  Stack,
  Typography,
  Chip,
  Button,
  TextField,
  MenuItem,
  Checkbox,
  ListItemText
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker } from 'app/components/elements/DatePicker';
import { useMemo, useState } from 'react';
import type { AdHocIssueSessionTechnician } from 'types/report';

export type IssueSessionRow = {
  id?: number;
  date?: string;
  timeIn?: string;
  timeOut?: string | null;
  materialsUsed?: string;
  equipmentUsed?: string;
  technicians?: AdHocIssueSessionTechnician[];
};

type UserOption = { value: string | number; label: string };

export const IssueSessionsSection = ({
  sessions,
  users,
  onSaveSession,
  onCreateSession,
  allowCreate = false
}: {
  sessions: IssueSessionRow[];
  users: UserOption[];
  onSaveSession: (
    sessionId: number,
    payload: {
      date?: string;
      timeIn?: string;
      timeOut?: string | null;
      materialsUsed?: string;
      equipmentUsed?: string;
      technicianUserIds?: number[];
    }
  ) => Promise<void>;
  onCreateSession?: (payload: {
    date?: string;
    timeIn?: string;
    timeOut?: string | null;
    materialsUsed?: string;
    equipmentUsed?: string;
    technicianUserIds?: number[];
  }) => Promise<void>;
  allowCreate?: boolean;
}) => {
  const [editingSessionId, setEditingSessionId] = useState<number | null>(null);
  const [savingSessionId, setSavingSessionId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingSessionValues, setEditingSessionValues] = useState<{
    date: Dayjs | null;
    timeIn: Dayjs | null;
    timeOut: Dayjs | null;
    materialsUsed: string;
    equipmentUsed: string;
    technicianUserIds: number[];
  }>({
    date: null,
    timeIn: null,
    timeOut: null,
    materialsUsed: '',
    equipmentUsed: '',
    technicianUserIds: []
  });

  const totalMinutesSpent = useMemo(() => {
    if (!Array.isArray(sessions)) return 0;
    return sessions.reduce((sum, s) => {
      if (s.timeIn && s.timeOut) {
        const diff = dayjs(s.timeOut).diff(dayjs(s.timeIn), 'minute');
        return sum + (diff > 0 ? diff : 0);
      }
      return sum;
    }, 0);
  }, [sessions]);

  const formatTotalTimeString = (totalMinutes: number) => {
    const hrs = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
  };

  const beginEditSession = (session: IssueSessionRow) => {
    const date = session.date ? dayjs(session.date) : dayjs();
    const timeIn = session.timeIn ? dayjs(session.timeIn) : null;
    const timeOut = session.timeOut ? dayjs(session.timeOut) : null;
    setEditingSessionId(session.id ?? null);
    setEditingSessionValues({
      date,
      timeIn,
      timeOut,
      materialsUsed: session.materialsUsed || '',
      equipmentUsed: session.equipmentUsed || '',
      technicianUserIds: Array.isArray(session.technicians)
        ? session.technicians
            .map((t) => t?.id ?? t?.userId)
            .filter((id): id is number => typeof id === 'number' && !Number.isNaN(id))
        : []
    });
  };

  const beginCreateSession = () => {
    setIsCreating(true);
    setEditingSessionId(null);
    setEditingSessionValues({
      date: dayjs(),
      timeIn: dayjs(),
      timeOut: null,
      materialsUsed: '',
      equipmentUsed: '',
      technicianUserIds: []
    });
  };

  const handleSessionFieldChange = (field: 'date' | 'timeIn' | 'timeOut', value: Dayjs | null) => {
    setEditingSessionValues((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = (session: IssueSessionRow) => {
    const baseDate = editingSessionValues.date || (session.date ? dayjs(session.date) : null);
    const baseTimeIn =
      editingSessionValues.timeIn || (session.timeIn ? dayjs(session.timeIn) : null);
    const baseTimeOut =
      editingSessionValues.timeOut || (session.timeOut ? dayjs(session.timeOut) : null);

    const payload: {
      date?: string;
      timeIn?: string;
      timeOut?: string | null;
      materialsUsed?: string;
      equipmentUsed?: string;
      technicianUserIds?: number[];
    } = {};

    if (baseDate) payload.date = baseDate.toISOString();
    if (baseTimeIn) payload.timeIn = baseTimeIn.toISOString();
    if (baseTimeOut) payload.timeOut = baseTimeOut.toISOString();
    else if (session.timeOut === null) payload.timeOut = null;

    payload.materialsUsed = editingSessionValues.materialsUsed;
    payload.equipmentUsed = editingSessionValues.equipmentUsed;
    payload.technicianUserIds = editingSessionValues.technicianUserIds;
    return payload;
  };

  const handleSaveSession = async (session: IssueSessionRow) => {
    if (!session.id) return;
    const baseDate = editingSessionValues.date || (session.date ? dayjs(session.date) : null);
    const baseTimeIn =
      editingSessionValues.timeIn || (session.timeIn ? dayjs(session.timeIn) : null);
    const baseTimeOut =
      editingSessionValues.timeOut || (session.timeOut ? dayjs(session.timeOut) : null);

    if (!baseDate || !baseTimeIn) return;
    const sessionHasEnded = !!session.timeOut;
    if (sessionHasEnded && !baseTimeOut) return;

    setSavingSessionId(session.id);
    try {
      await onSaveSession(session.id, buildPayload(session));
      setEditingSessionId(null);
    } finally {
      setSavingSessionId(null);
    }
  };

  const handleCreate = async () => {
    if (!onCreateSession) return;
    if (!editingSessionValues.date || !editingSessionValues.timeIn) return;
    setSavingSessionId(-1);
    try {
      await onCreateSession({
        date: editingSessionValues.date.toISOString(),
        timeIn: editingSessionValues.timeIn.toISOString(),
        timeOut: editingSessionValues.timeOut?.toISOString() ?? null,
        materialsUsed: editingSessionValues.materialsUsed,
        equipmentUsed: editingSessionValues.equipmentUsed,
        technicianUserIds: editingSessionValues.technicianUserIds
      });
      setIsCreating(false);
    } finally {
      setSavingSessionId(null);
    }
  };

  const renderSessionEditor = (session?: IssueSessionRow) => (
    <Stack gap={1}>
      <Stack gap={0.25}>
        <Typography variant="caption" color="text.secondary">
          DATE
        </Typography>
        <DatePicker
          title=""
          value={editingSessionValues.date}
          onChange={(next) => handleSessionFieldChange('date', next)}
          dateStyle={{
            '& .MuiInputBase-root': { minHeight: 40, height: 40, borderRadius: 1 }
          }}
        />
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
        <Stack gap={0.25} flex={1}>
          <Typography variant="caption" color="text.secondary">
            TIME IN
          </Typography>
          <TextField
            type="time"
            size="small"
            fullWidth
            value={editingSessionValues.timeIn ? editingSessionValues.timeIn.format('HH:mm') : ''}
            onChange={(e) =>
              handleSessionFieldChange(
                'timeIn',
                e.target.value
                  ? dayjs(
                      (editingSessionValues.date || dayjs()).format('YYYY-MM-DD') +
                        `T${e.target.value}`
                    )
                  : null
              )
            }
          />
        </Stack>
        <Stack gap={0.25} flex={1}>
          <Typography variant="caption" color="text.secondary">
            TIME OUT
          </Typography>
          <TextField
            type="time"
            size="small"
            fullWidth
            value={editingSessionValues.timeOut ? editingSessionValues.timeOut.format('HH:mm') : ''}
            onChange={(e) =>
              handleSessionFieldChange(
                'timeOut',
                e.target.value
                  ? dayjs(
                      (editingSessionValues.date || dayjs()).format('YYYY-MM-DD') +
                        `T${e.target.value}`
                    )
                  : null
              )
            }
          />
        </Stack>
      </Stack>
      <Stack gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          MATERIALS USED
        </Typography>
        <TextField
          size="small"
          multiline
          minRows={2}
          fullWidth
          value={editingSessionValues.materialsUsed}
          onChange={(e) =>
            setEditingSessionValues((prev) => ({ ...prev, materialsUsed: e.target.value }))
          }
        />
      </Stack>
      <Stack gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          EQUIPMENT USED
        </Typography>
        <TextField
          size="small"
          multiline
          minRows={2}
          fullWidth
          value={editingSessionValues.equipmentUsed}
          onChange={(e) =>
            setEditingSessionValues((prev) => ({ ...prev, equipmentUsed: e.target.value }))
          }
        />
      </Stack>
      <Stack gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          OTHER TECHNICIANS PRESENT
        </Typography>
        <TextField
          select
          SelectProps={{
            multiple: true,
            renderValue: (selected) => {
              const ids = Array.isArray(selected) ? (selected as number[]) : [];
              const names = ids
                .map((id) => users?.find((u) => String(u.value) === String(id))?.label)
                .filter(Boolean);
              return names.length ? names.join(', ') : '—';
            }
          }}
          size="small"
          value={editingSessionValues.technicianUserIds}
          onChange={(e) => {
            const v = e.target.value as unknown;
            setEditingSessionValues((prev) => ({
              ...prev,
              technicianUserIds: Array.isArray(v) ? (v as number[]) : []
            }));
          }}>
          {(users || []).map((u) => {
            const id = Number(u.value);
            return (
              <MenuItem key={String(u.value)} value={id}>
                <Checkbox checked={editingSessionValues.technicianUserIds.includes(id)} />
                <ListItemText primary={u.label} />
              </MenuItem>
            );
          })}
        </TextField>
      </Stack>
      <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={() => {
            setEditingSessionId(null);
            setIsCreating(false);
          }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={savingSessionId != null}
          onClick={() => (isCreating ? handleCreate() : session && handleSaveSession(session))}>
          {savingSessionId != null ? 'Saving...' : 'Save'}
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography fontWeight={700} color="#226AAA">
          Sessions
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Chip
            size="small"
            label={`${sessions?.length ?? 0} logs found`}
            sx={{ fontSize: 10, fontWeight: 700, bgcolor: '#FFF3E0', color: '#FB8C00' }}
          />
          {allowCreate && onCreateSession && (
            <Button size="small" variant="outlined" onClick={beginCreateSession}>
              Add session
            </Button>
          )}
        </Stack>
      </Stack>
      {sessions.length > 0 && totalMinutesSpent >= 0 && (
        <Typography variant="caption" color="#226AAA" fontWeight={700} textAlign="right">
          Total hours spent: {formatTotalTimeString(totalMinutesSpent)} (Completed sessions)
        </Typography>
      )}
      {isCreating && renderSessionEditor()}
      {sessions.length
        ? sessions.map((session, index) => {
            const isEditing = editingSessionId === session.id;
            return (
              <Stack
                key={session.id ?? index}
                gap={1}
                sx={{ borderRadius: 1.5, border: '1px solid #E5E7EB', p: 1.5, bgcolor: '#F9FAFB' }}>
                {!isEditing ? (
                  <>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      gap={0.25}>
                      <Stack gap={0.25} flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          DATE
                        </Typography>
                        <Typography variant="subtitle2">
                          {session.date ? dayjs(session.date).format('YYYY-MM-DD') : '-'}
                        </Typography>
                      </Stack>
                      <Stack gap={0.25} flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          TIME IN
                        </Typography>
                        <Typography variant="subtitle2">
                          {session.timeIn ? dayjs(session.timeIn).format('HH:mm') : '-'}
                        </Typography>
                      </Stack>
                      <Stack gap={0.25} flex={1}>
                        <Typography variant="caption" color="text.secondary">
                          TIME OUT
                        </Typography>
                        <Typography variant="subtitle2">
                          {session.timeOut ? dayjs(session.timeOut).format('HH:mm') : 'In progress'}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Typography variant="body2">
                      Materials: {session.materialsUsed?.trim() || 'None'}
                    </Typography>
                    <Typography variant="body2">
                      Equipment: {session.equipmentUsed?.trim() || 'None'}
                    </Typography>
                    <Stack direction="row" justifyContent="flex-end">
                      <Button size="small" variant="text" onClick={() => beginEditSession(session)}>
                        Edit
                      </Button>
                    </Stack>
                  </>
                ) : (
                  renderSessionEditor(session)
                )}
              </Stack>
            );
          })
        : !isCreating && (
            <Typography variant="caption" color="text.secondary">
              No sessions recorded yet.
            </Typography>
          )}
    </Stack>
  );
};
